import { Link, useSearchParams } from 'react-router';
import { CHAPTERS, GLOSSARY, INTRODUCTION, ROLES, TOPICS, adaptList, adaptRecord, topicsForStructure, type Structure, type Topic } from '@syndiqia/manual';
import { AppFooter } from '../components/AppFooter';
import { Logo } from '../components/Logo';
import { useI18n } from '../i18n';

/** What co-owners and occupants may do: topics needing anything else are for the board. */
const MEMBER_PERMISSIONS = new Set(['requests.create', 'documents.read', 'agents.ask', 'governance.view', 'reservations.book', 'insurance.submit']);
const forMembers = (t: Topic) => t.requires === null || MEMBER_PERMISSIONS.has(t.requires);

/** The complete user manual: introduction, roles, every chapter and task, glossary. Printable. */
export function ManualPage() {
  const { t, locale, setLocale } = useI18n();
  const m = t.manual;
  // Written for syndicates; ?structure=association shows the owners' association wording.
  const [params, setParams] = useSearchParams();
  const structure: Structure = params.get('structure') === 'association' ? 'npo_with_servitudes' : 'divided_coownership';
  const topics = topicsForStructure(TOPICS, structure);
  const chapters = CHAPTERS.map((c) => ({ ...c, intro: adaptRecord(c.intro, structure), topics: topics.filter((x) => x.page === c.page) })).filter((c) => c.topics.length > 0);
  const introduction = adaptList(INTRODUCTION, structure);
  const roles = ROLES.map((r) => ({ role: adaptRecord(r.role, structure), can: adaptRecord(r.can, structure) }));
  const glossary = GLOSSARY.map((g) => ({ term: adaptRecord(g.term, structure), definition: adaptRecord(g.definition, structure) }));

  return (
    <div className="manual">
      <header className="topbar">
        <Link to="/" aria-label={m.back}>
          <Logo />
        </Link>
        <div className="topbar-actions">
          <button type="button" className="link-button" onClick={() => window.print()}>
            {m.print}
          </button>
          <button type="button" className="link-button" onClick={() => setLocale(locale === 'fr' ? 'en' : 'fr')}>
            {t.language}
          </button>
          <Link to="/" className="link-button">
            {m.back}
          </Link>
        </div>
      </header>
      <main className="content manual-content">
        <h1>{m.title}</h1>
        <div className="segmented manual-version" role="group" aria-label={m.version}>
          {(['divided_coownership', 'npo_with_servitudes'] as const).map((k) => (
            <button key={k} type="button" aria-pressed={structure === k} className={structure === k ? 'active' : undefined} onClick={() => setParams(k === 'npo_with_servitudes' ? { structure: 'association' } : {}, { replace: true })}>
              {t.chooseKinds[k]}
            </button>
          ))}
        </div>
        <nav className="panel manual-toc" aria-label={m.contents}>
          <h2>{m.contents}</h2>
          <ol>
            <li>
              <a href="#introduction">{m.introduction}</a>
            </li>
            <li>
              <a href="#roles">{m.roles}</a>
            </li>
            {chapters.map((c) => (
              <li key={c.page}>
                <a href={`#chapter-${c.page}`}>{c.title[locale]}</a>
              </li>
            ))}
            <li>
              <a href="#glossary">{m.glossary}</a>
            </li>
          </ol>
        </nav>

        <section id="introduction" className="manual-chapter">
          <h2>{m.introduction}</h2>
          {introduction[locale].map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </section>

        <section id="roles" className="manual-chapter">
          <h2>{m.roles}</h2>
          <table className="table table-stack">
            <thead>
              <tr>
                <th>{m.role}</th>
                <th>{m.canDo}</th>
              </tr>
            </thead>
            <tbody>
              {roles.map((r) => (
                <tr key={r.role.fr}>
                  <td data-label={m.role}>
                    <strong>{r.role[locale]}</strong>
                  </td>
                  <td data-label={m.canDo}>{r.can[locale]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {chapters.map((c, n) => (
          <section key={c.page} id={`chapter-${c.page}`} className="manual-chapter">
            <h2>
              {n + 1}. {c.title[locale]}
            </h2>
            <p className="muted">{c.intro[locale]}</p>
            {c.topics.map((x) => (
              <article key={x.id} id={x.id} className="manual-topic">
                <h3>
                  {x.title[locale]} <span className={`badge ${forMembers(x) ? 'badge-audience-all' : 'badge-audience-board'}`}>{forMembers(x) ? m.everyone : m.board}</span>
                </h3>
                <ol>
                  {x.body[locale].map((p, i) => (
                    <li key={i}>{p}</li>
                  ))}
                </ol>
              </article>
            ))}
          </section>
        ))}

        <section id="glossary" className="manual-chapter">
          <h2>{m.glossary}</h2>
          <dl className="manual-glossary">
            {glossary.map((g) => (
              <div key={g.term.fr}>
                <dt>{g.term[locale]}</dt>
                <dd>{g.definition[locale]}</dd>
              </div>
            ))}
          </dl>
        </section>
      </main>
      <AppFooter />
    </div>
  );
}
